import { get, post, authFetch } from '../request';

// ─── Types ──────────────────────────────────────────────

export interface PlayableListItem {
  id: string;
  name: string | null;
  version: number;
  variantLabel: string | null;
  /** 对话描述（生成/微调指令摘要） */
  description: string | null;
  generationMode: string | null;
  /** 创建方式：manual（手动生成）/ variant（批量变体），由后端判定 */
  generationMethod?: 'manual' | 'variant';
  previewUrl: string | null;
  /** 封面缩略图 URL（cover.png，与 previewUrl 同目录派生；异步生成，可能晚于版本就绪） */
  coverUrl?: string | null;
  fileSize: number | null;
  quality: { score?: number; [k: string]: unknown } | null;
  status: string;
  createdAt: string;
  /** 可空：已发布试玩是独立资产，项目/会话删除后 sessionId 置 null 仍保留 */
  sessionId: string | null;
  sourcePlayableId: string | null;
  sessionTitle: string | null;
  projectId?: string | null;
  projectName?: string | null;
  /** 变体维度 patch：该变体相对源试玩修改了哪些配置（仅批量变体返回） */
  variantPatch?: Record<string, any> | null;
  /** 源试玩配置：用于展示原始值对比（仅批量任务查询时返回） */
  sourceConfig?: Record<string, any> | null;
  /** 投放平台发布记录（列表接口聚合返回，按环境去重展示徽标） */
  platformPublishes?: PlatformPublishInfo[];
}

export interface PlayablePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** 投放平台发布记录（后端聚合返回；投放平台无删除接口，撤回不联动） */
export interface PlatformPublishInfo {
  env: 'prod' | 'test';
  materialName: string;
  publishedAt: string;
}

// ─── APIs ───────────────────────────────────────────────

/** 按项目查询试玩列表 */
export function getProjectPlayables(projectId: string): Promise<{ success: boolean; data: PlayableListItem[] }> {
  return get<{ success: boolean; data: PlayableListItem[] }>(`/api/playables?projectId=${projectId}`);
}

/** 按会话查询试玩列表 */
export function getSessionPlayables(sessionId: string): Promise<{ success: boolean; data: PlayableListItem[] }> {
  return get<{ success: boolean; data: PlayableListItem[] }>(`/api/playables?sessionId=${sessionId}`);
}

/** 全局试玩列表（分页） */
export function getAllPlayables(params?: {
  page?: number;
  limit?: number;
  projectId?: string;
  generationMethod?: 'manual' | 'variant';
  generationMode?: string;
  status?: string;
  search?: string;
}): Promise<{ success: boolean; data: PlayableListItem[]; pagination: PlayablePagination }> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.projectId) search.set('projectId', params.projectId);
  if (params?.generationMethod) search.set('generationMethod', params.generationMethod);
  if (params?.generationMode) search.set('generationMode', params.generationMode);
  if (params?.status) search.set('status', params.status);
  if (params?.search) search.set('search', params.search);
  const qs = search.toString();
  return get<{ success: boolean; data: PlayableListItem[]; pagination: PlayablePagination }>(`/api/playables/all${qs ? `?${qs}` : ''}`);
}

/** 按批量任务ID查询变体列表 */
export function getBatchJobPlayables(batchJobId: string): Promise<{ success: boolean; data: PlayableListItem[] }> {
  return get<{ success: boolean; data: PlayableListItem[] }>(`/api/playables?batchJobId=${batchJobId}`);
}

/** 单个试玩详情 */
export function getPlayable(id: string): Promise<{ success: boolean; data: PlayableListItem }> {
  return get<{ success: boolean; data: PlayableListItem }>(`/api/playables/${id}`);
}

/** 更新试玩元数据 */
export function updatePlayable(id: string, data: { variantLabel?: string; status?: string; generationMode?: string }): Promise<void> {
  return authFetch(`/api/playables/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(() => undefined);
}

/** 删除试玩 */
export function deletePlayable(id: string): Promise<void> {
  return authFetch(`/api/playables/${id}`, { method: 'DELETE' }).then(() => undefined);
}

/** 发布后清空历史版本：仅保留 keepPlayableId 基准，删除其他 DB 记录与磁盘临时产物 */
export function cleanupHistoryVersions(
  sessionId: string,
  keepPlayableId: string,
): Promise<{ success: boolean; deletedPlayables: number; deletedEntries: string[] }> {
  return authFetch(`/api/playable/${sessionId}/cleanup-history`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ keepPlayableId }),
  }).then(r => r.json());
}

/** 将未挂靠项目的试玩（sandbox）挂载到指定项目（后端只改 DB 归属，不移动 OSS 对象） */
export function attachPlayable(id: string, projectId: string): Promise<{ success: boolean; message?: string }> {
  return post<{ success: boolean; message?: string }>(`/api/playables/${id}/attach`, { projectId });
}

/**
 * 发布试玩到投放平台（adputting /game_list 试玩素材库）。
 * env: 'prod' 线上投放平台 / 'test' 测试环境走不同 host。
 * ⚠️ 投放平台无删除试玩素材接口 —— 发布后不可撤回（弹窗已明确提示）。
 */
export function publishPlayableToPlatform(
  id: string,
  env: 'prod' | 'test',
): Promise<{
  success: boolean;
  data?: { env: 'prod' | 'test'; materialName: string; materialId: number | null };
  message?: string;
}> {
  return post(`/api/playables/${id}/publish-to-platform`, { env });
}

/**
 * 构造分享短链（P4）：/api/p/{12位hex分享ID} 由后端 302 跳转到实际预览地址。
 * 对外复制/扫码一律用短链——URL 与 OSS 物理路径解耦，存储位置变更不失效。
 *
 * BUG-20260813-003: 仅当 playableId 为合法 12 位 hex 分享 ID 时才生成短链。
 * 微调版本（name=refine-xxx）等非落库分享 ID 的产物没有对应 /api/p 路由（后端严格
 * 校验 /^[a-f0-9]{12}$/i，非法值一律 404），必须返回 null 让调用方退回预览直链，
 * 否则复制/扫码得到的是 /api/p/refine-xxx 这种打不开的链接。
 */
export function buildShareUrl(playableId: string | null | undefined): string | null {
  if (!playableId || !/^[a-f0-9]{12}$/i.test(playableId)) return null;
  return `${window.location.origin}/api/p/${playableId}`;
}
